import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toJpeg } from 'html-to-image'
import { api, apiImage, ApiError } from '@shop/api-client'
import type { Product, ProductListItem } from '../api/types'
import { Banner, Button, ErrorBanner, Field, Panel, Spinner, TextArea, TextInput } from '@shop/ui'
import { PostDesign, SIZES, SWATCHES, TEMPLATES } from '../social/templates'
import type { Design, SizeKey, TemplateKey } from '../social/templates'
import { platformName, socialPosts, socialStatus, toDataUrl } from '../social/api'
import type { SocialPost } from '../social/api'
import { PostResults } from '../social/PostResults'

type Publishing = { showOnWebsite: boolean; slug: string | null }
type OfferRow = { productId: string; offerPrice: number; regularPrice: number; endsAt: string }

/** شريحةٌ في المنشور — صنفٌ بصورته وسعره. الأولى الصنفُ الأساس، والباقي ألبوم. */
type Slide = {
  productId: string
  name: string
  brand: string | null
  price: number | null
  regular: number | null
  image: string | null
}

const SITE = 'https://aqabamobile.com'
const SHOP = 'عال العال للموبايل · شويخ مول، العقبة'
const MAX_SLIDES = 10

/** ألوانُ كلّ قالبٍ ابتداءً — والمالكُ يغيّرها. */
const LOOK: Record<TemplateKey, { background: string; accent: string; badge: string }> = {
  classic: { background: '#ffffff', accent: '#f0a202', badge: '' },
  offer: { background: '#191a1e', accent: '#fde00a', badge: 'عرض' },
  new: { background: '#191a1e', accent: '#f79705', badge: 'وصل حديثًا' },
}

const money = (v: number) => `${v.toFixed(3).replace(/\.?0+$/, '')} دينار`

/** أقلُّ سعرٍ في متغيّرات الصنف — «ابتداءً من». */
function lowestPrice(p: Product): number | null {
  const prices = p.variants.map((v) => v.currentPrice).filter((v): v is number => v !== null)

  return prices.length ? Math.min(...prices) : null
}

/** صنفٌ ⇐ شريحة: اسمُه وسعرُه (بعرضه إن وُجد) وصورتُه data: URL. */
async function slideOf(p: Product, offers: OfferRow[]): Promise<Slide> {
  const offer = offers.find((o) => o.productId === p.id) ?? null
  const blob = p.hasImage ? await apiImage(`/api/v1/products/${p.id}/image`).catch(() => null) : null

  return {
    productId: p.id,
    name: p.nameAr,
    brand: p.brandName,
    price: offer?.offerPrice ?? lowestPrice(p),
    regular: offer?.regularPrice ?? null,
    image: blob ? await toDataUrl(blob) : null,
  }
}

/**
 * نصُّ المنشور الافتراضيّ — <b>من حقائق الصنف وحدَها</b>: لا وعدَ ولا صفةَ لم يقلها المالك.
 * وللألبوم سطرٌ لكلّ صنفٍ بسعره. ويُعدَّل كلُّه قبل النشر.
 */
function draftCaption(p: Product, slides: Slide[], showPrice: boolean, extra: string, link: string | null) {
  const [first] = slides
  const lines = [p.nameAr]

  if (slides.length > 1) {
    lines.push('')
    for (const s of slides) {
      lines.push(showPrice && s.price !== null ? `• ${s.name} — ${money(s.price)}` : `• ${s.name}`)
    }
  } else {
    if (p.brandName) lines.push(p.brandName)
    if (p.description) lines.push('', p.description.length > 300 ? `${p.description.slice(0, 297)}…` : p.description)
    if (p.warrantyMonths) lines.push(`كفالة ${p.warrantyMonths} شهر`)

    if (showPrice && first?.price != null) {
      lines.push('', first.regular !== null && first.regular > first.price
        ? `🔥 بدل ${money(first.regular)} — الآن ${money(first.price)} لفترة محدودة`
        : `السعر: ${money(first.price)}`)
    }
  }

  if (extra.trim()) lines.push('', extra.trim())

  lines.push('', `📍 ${SHOP}`)
  if (link) lines.push(`🛒 اطلبه من الموقع: ${link}`)
  lines.push('📞 واتساب: 0797051992')

  const tags = ['#العقبة', '#اكسسوارات_موبايل', p.categoryName ? `#${p.categoryName.replace(/\s+/g, '_')}` : null]
  lines.push('', tags.filter(Boolean).join(' '))

  return lines.join('\n')
}

/** «الآن + ساعة» بصيغة datetime-local بتوقيت الجهاز. */
function inAnHour(): string {
  const at = new Date(Date.now() + 3_600_000)
  const pad = (n: number) => String(n).padStart(2, '0')

  return `${at.getFullYear()}-${pad(at.getMonth() + 1)}-${pad(at.getDate())}T${pad(at.getHours())}:${pad(at.getMinutes())}`
}

/**
 * منشورٌ جديد — <b>المنتج ← التفاصيل ← التصميم ← المعاينة ← النشر (الآن أو لاحقًا)</b>.
 *
 * <p>
 * ⚠ <b>والصورُ تُرسم هنا لا على الخادم</b>: العُقدُ نفسُها التي يراها المالكُ تُحوَّل JPEG
 * وتُرفع — فالمعاينةُ هي المنشورُ حرفًا بحرف، والعربيّةُ يرسمها المتصفّح. وللألبوم شريحةٌ
 * لكلّ صنفٍ بالتصميم نفسِه.
 * </p>
 */
export default function SocialCompose() {
  const [params, setParams] = useSearchParams()
  const productId = params.get('productId')
  const queries = useQueryClient()
  const slideRefs = useRef<(HTMLDivElement | null)[]>([])

  const [term, setTerm] = useState('')
  const [albumTerm, setAlbumTerm] = useState('')
  const [template, setTemplate] = useState<TemplateKey | null>(null)
  const [size, setSize] = useState<SizeKey>('SQUARE')
  const [showPrice, setShowPrice] = useState(true)
  const [extra, setExtra] = useState('')
  const [headline, setHeadline] = useState<string | null>(null)
  const [subline, setSubline] = useState<string | null>(null)
  const [badge, setBadge] = useState<string | null>(null)
  const [accent, setAccent] = useState<string | null>(null)
  const [background, setBackground] = useState<string | null>(null)
  const [caption, setCaption] = useState<string | null>(null)
  const [chosen, setChosen] = useState<string[] | null>(null)
  const [main, setMain] = useState<Slide | null>(null)
  const [extras, setExtras] = useState<Slide[]>([])
  const [current, setCurrent] = useState(0)
  const [logo, setLogo] = useState<string | null>(null)
  const [result, setResult] = useState<SocialPost | null>(null)
  const [copied, setCopied] = useState(false)
  const [later, setLater] = useState(false)
  const [scheduleAt, setScheduleAt] = useState(inAnHour())

  const found = useQuery({
    queryKey: ['social-search', term.trim()],
    queryFn: () => api<ProductListItem[]>(`/api/v1/products?limit=12&q=${encodeURIComponent(term.trim())}`),
    enabled: !productId && term.trim().length >= 2,
    placeholderData: keepPreviousData,
  })

  const albumFound = useQuery({
    queryKey: ['social-search', albumTerm.trim()],
    queryFn: () => api<ProductListItem[]>(`/api/v1/products?limit=8&q=${encodeURIComponent(albumTerm.trim())}`),
    enabled: albumTerm.trim().length >= 2,
    placeholderData: keepPreviousData,
  })

  const product = useQuery({
    queryKey: ['product', productId],
    queryFn: () => api<Product>(`/api/v1/products/${productId}`),
    enabled: !!productId,
  })

  const publishing = useQuery({
    queryKey: ['publishing', productId],
    queryFn: () => api<Publishing>(`/api/v1/products/${productId}/publishing`),
    enabled: !!productId,
  })

  const offers = useQuery({ queryKey: ['offers'], queryFn: () => api<OfferRow[]>('/api/v1/offers'), enabled: !!productId })
  const status = useQuery({ queryKey: ['social-status'], queryFn: socialStatus })
  const history = useQuery({
    queryKey: ['social-posts', productId],
    queryFn: () => socialPosts(productId!),
    enabled: !!productId,
  })

  // الشعارُ data: URL — فيُرسم في JPEG بلا قيود المصدر.
  useEffect(() => {
    let live = true

    fetch('/brand-logo.png').then((r) => (r.ok ? r.blob() : null)).then(async (b) => {
      if (b && live) setLogo(await toDataUrl(b))
    }).catch(() => {})

    return () => {
      live = false
    }
  }, [])

  // الشريحةُ الأولى من الصنف الأساس — بعد أن تصل العروض، فيُعرض سعرُ العرض إن وُجد.
  useEffect(() => {
    let live = true

    if (product.data && offers.data) {
      void slideOf(product.data, offers.data).then((s) => {
        if (live) setMain(s)
      })
    }

    return () => {
      live = false
    }
  }, [product.data, offers.data])

  const addToAlbum = useMutation({
    mutationFn: async (id: string) => {
      const p = await api<Product>(`/api/v1/products/${id}`)

      return slideOf(p, offers.data ?? [])
    },
    onSuccess: (slide) => {
      setExtras((xs) => [...xs, slide])
      setAlbumTerm('')
      setCaption(null)
    },
  })

  const p = product.data
  const slides = useMemo(() => (main ? [main, ...extras] : []), [main, extras])
  const album = slides.length > 1
  const story = size === 'STORY'
  const link = publishing.data?.showOnWebsite && publishing.data.slug
    ? `${SITE}/p/${encodeURIComponent(publishing.data.slug)}`
    : null

  const offered = main?.regular != null && main.price != null && main.regular > main.price
  const tpl: TemplateKey = template ?? (offered ? 'offer' : 'classic')
  const look = LOOK[tpl]

  /** تصميمُ الشريحة — النصوصُ المعدَّلةُ للأولى، والألوانُ والقالبُ للكلّ. */
  const designFor = (s: Slide, index: number): Design => {
    const saving = s.regular != null && s.price != null && s.regular > s.price
      ? Math.round((1 - s.price / s.regular) * 100)
      : 0

    return {
      template: tpl,
      size,
      headline: index === 0 ? headline ?? s.name : s.name,
      subline: index === 0 ? subline ?? s.brand ?? '' : s.brand ?? '',
      price: s.price,
      regularPrice: s.regular,
      showPrice,
      badge: index === 0 && badge !== null ? badge : tpl === 'offer' && saving > 0 ? `وفّر ${saving}%` : look.badge,
      accent: accent ?? look.accent,
      background: background ?? look.background,
      footer: SHOP,
      image: s.image,
      logo,
    }
  }

  const text = caption ?? (p ? draftCaption(p, slides, showPrice, extra, link) : '')
  const accounts = (status.data?.accounts ?? []).filter((a) => a.connected)
  const targets = chosen ?? accounts.filter((a) => a.enabled).map((a) => a.id)

  const render = async (index: number): Promise<Blob> => {
    const node = slideRefs.current[index]?.firstElementChild as HTMLElement | null

    if (!node) throw new Error('لا تصميمَ لِيُرسم.')

    const { w, h } = SIZES[size]
    const url = await toJpeg(node, { width: w, height: h, pixelRatio: 1, quality: 0.92, cacheBust: true })

    return (await fetch(url)).blob()
  }

  const download = async () => {
    for (let i = 0; i < (story ? 1 : slides.length); i++) {
      const blob = await render(i)
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${p?.code ?? 'post'}-${size.toLowerCase()}${slides.length > 1 ? `-${i + 1}` : ''}.jpg`
      a.click()
      URL.revokeObjectURL(a.href)
    }
  }

  const send = useMutation({
    mutationFn: async (mode: 'now' | 'later') => {
      const post = await api<SocialPost>('/api/v1/social/posts', {
        method: 'POST',
        body: { productId, caption: text, template: tpl, size, accountIds: targets },
      })

      // ⚠ صورةٌ لكلّ شريحةٍ بموضعها وصنفها — والستوري الأولى وحدَها.
      const count = story ? 1 : slides.length

      for (let i = 0; i < count; i++) {
        const form = new FormData()
        form.append('file', new File([await render(i)], `slide-${i}.jpg`, { type: 'image/jpeg' }))
        await api<void>(`/api/v1/social/posts/${post.id}/image?position=${i}&productId=${slides[i].productId}`, {
          method: 'POST',
          form,
        })
      }

      const go = (confirmDuplicate: boolean) => mode === 'now'
        ? api<SocialPost>(`/api/v1/social/posts/${post.id}/publish`, { method: 'POST', body: { confirmDuplicate } })
        : api<SocialPost>(`/api/v1/social/posts/${post.id}/schedule`, {
            method: 'POST',
            body: { at: new Date(scheduleAt).toISOString(), confirmDuplicate },
          })

      try {
        return await go(false)
      } catch (error) {
        // ⚠ نُشر صنفٌ منه خلال أسبوع — يُسأل المالك، ولا يُكرَّر بلا إقرار.
        if (error instanceof ApiError && error.code === 'social.duplicate_recent'
            && window.confirm(`${error.message}\n\nانشره مع ذلك؟`)) {
          return go(true)
        }

        throw error
      }
    },
    onSuccess: async (post) => {
      setResult(post)
      await queries.invalidateQueries({ queryKey: ['social-posts'] })
    },
  })

  // ── لا صنف: اختيارُه ─────────────────────────────────────────────────────
  if (!productId) {
    return (
      <Panel title="منشورٌ جديد — اختر الصنف">
        <Field label="الصنف"><TextInput value={term} onChange={setTerm} placeholder="ابحث بالاسم أو الرمز…" /></Field>
        {found.isFetching && <Spinner />}
        <ul className="mt-2 grid gap-1">
          {(term.trim().length >= 2 ? found.data ?? [] : []).map((x) => (
            <li key={x.id}>
              <button
                type="button"
                className="w-full rounded-lg border border-line px-3 py-2 text-right hover:bg-surface-raised"
                onClick={() => setParams({ productId: x.id })}
              >
                {x.nameAr}
              </button>
            </li>
          ))}
        </ul>
      </Panel>
    )
  }

  if (!p || !main) {
    return <Panel title="منشورٌ جديد">{product.error ? <ErrorBanner error={product.error} /> : <Spinner />}</Panel>
  }

  const { w, h } = SIZES[size]
  const scale = 380 / w
  const shown = Math.min(current, slides.length - 1)
  const last = history.data?.find((x) => x.targets.some((t) => t.status === 'PUBLISHED'))

  return (
    <>
      <Panel
        title={`منشورٌ: ${p.nameAr}`}
        actions={<Link className="text-sm underline" to="/social">حساباتُ التواصل</Link>}
      >
        {last && (
          <Banner tone="warn">
            نُشر هذا الصنفُ من قبل ({new Date(last.createdAt).toLocaleDateString('ar-JO')}) — انظر السجلَّ أسفل الصفحة.
          </Banner>
        )}
        {!p.hasImage && <Banner tone="warn">الصنفُ بلا صورة — يُرسم التصميمُ بشعار المحلّ. أضف صورةً من صفحة الصنف لنتيجةٍ أفضل.</Banner>}

        <div className="grid gap-6 lg:grid-cols-[1fr_400px]">
          {/* ── التفاصيل ─────────────────────────────────────────────── */}
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2">
              {(Object.keys(TEMPLATES) as TemplateKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => { setTemplate(key); setBadge(null); setAccent(null); setBackground(null) }}
                  className={`rounded-full border px-4 py-1.5 text-sm ${tpl === key ? 'border-brand bg-brand text-white' : 'border-line'}`}
                >
                  {TEMPLATES[key]}
                </button>
              ))}
            </div>

            <div className="flex flex-wrap gap-2">
              {(Object.keys(SIZES) as SizeKey[]).map((key) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setSize(key)}
                  className={`rounded-full border px-4 py-1.5 text-sm ${size === key ? 'border-brand bg-brand text-white' : 'border-line'}`}
                >
                  {SIZES[key].label}
                </button>
              ))}
            </div>

            <div className="grid gap-3 sm:grid-cols-2">
              <Field label="العنوان"><TextInput value={headline ?? main.name} onChange={setHeadline} /></Field>
              <Field label="سطرٌ تحته"><TextInput value={subline ?? main.brand ?? ''} onChange={setSubline} /></Field>
              <Field label="الشارة" hint="فارغة = بلا شارة">
                <TextInput value={designFor(main, 0).badge} onChange={setBadge} />
              </Field>
              <Field label="عرضٌ أو تفصيلٌ للنصّ" hint="يُضاف إلى نصّ المنشور">
                <TextInput value={extra} onChange={(v) => { setExtra(v); setCaption(null) }} placeholder="مثال: هديّة مع كلّ قطعة" />
              </Field>
            </div>

            <label className="flex items-center gap-2 text-sm">
              <input type="checkbox" checked={showPrice} onChange={(e) => { setShowPrice(e.target.checked); setCaption(null) }} />
              إظهارُ السعر {main.price === null && <span className="text-warn">(لا سعرَ للصنف)</span>}
            </label>

            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span>اللونُ المميِّز</span>
              <Swatches value={accent ?? look.accent} onChange={setAccent} />
            </div>
            <div className="flex flex-wrap items-center gap-4 text-sm">
              <span>الخلفيّة</span>
              <Swatches value={background ?? look.background} onChange={setBackground} />
            </div>

            {/* ── الألبوم ─────────────────────────────────────────────── */}
            {!story && (
              <div className="rounded-lg border border-line p-3">
                <div className="mb-2 text-sm font-semibold">
                  ألبوم {album ? `· ${slides.length} أصناف` : '(اختياريّ) — أصنافٌ أخرى في المنشور نفسِه'}
                </div>

                {extras.length > 0 && (
                  <ul className="mb-2 flex flex-wrap gap-1.5">
                    {extras.map((s, i) => (
                      <li key={s.productId} className="flex items-center gap-1 rounded-full bg-surface-raised px-3 py-1 text-sm">
                        {s.name}
                        <button
                          type="button"
                          aria-label="إزالة"
                          className="text-ink-soft"
                          onClick={() => { setExtras(extras.filter((_, j) => j !== i)); setCaption(null); setCurrent(0) }}
                        >
                          ×
                        </button>
                      </li>
                    ))}
                  </ul>
                )}

                {slides.length < MAX_SLIDES && (
                  <TextInput value={albumTerm} onChange={setAlbumTerm} placeholder="أضف صنفًا للألبوم…" />
                )}

                <ul className="mt-1 grid gap-1">
                  {(albumTerm.trim().length >= 2 ? albumFound.data ?? [] : [])
                    .filter((x) => !slides.some((s) => s.productId === x.id))
                    .slice(0, 6)
                    .map((x) => (
                      <li key={x.id}>
                        <button
                          type="button"
                          disabled={addToAlbum.isPending}
                          className="w-full rounded-lg border border-line px-3 py-1.5 text-right text-sm hover:bg-surface-raised"
                          onClick={() => addToAlbum.mutate(x.id)}
                        >
                          + {x.nameAr}
                        </button>
                      </li>
                    ))}
                </ul>
                <ErrorBanner error={addToAlbum.error} />
              </div>
            )}

            <Field label="نصُّ المنشور" hint={caption === null ? 'مُعَدٌّ من بيانات الصنف — عدّله كما تشاء' : 'معدَّلٌ بيدك'}>
              <TextArea value={text} onChange={setCaption} rows={12} />
            </Field>
            {caption !== null && (
              <button type="button" className="text-xs underline" onClick={() => setCaption(null)}>أعِد النصَّ الافتراضيّ</button>
            )}
            {story && <p className="text-xs text-ink-soft">الستوري تُنشر صورةً بلا نصّ — والنصُّ هنا للنسخ فقط.</p>}
          </div>

          {/* ── المعاينة ────────────────────────────────────────────── */}
          <div className="space-y-3">
            <div
              className="overflow-hidden rounded-xl border border-line"
              style={{ width: w * scale, height: h * scale, direction: 'ltr' }}
            >
              <div style={{ transform: `scale(${scale})`, transformOrigin: 'top left', width: w, height: h }}>
                <PostDesign d={designFor(slides[shown], shown)} />
              </div>
            </div>

            {album && !story && (
              <div className="flex flex-wrap gap-1">
                {slides.map((s, i) => (
                  <button
                    key={s.productId}
                    type="button"
                    onClick={() => setCurrent(i)}
                    className={`rounded px-2 py-0.5 text-xs ${i === shown ? 'bg-brand text-white' : 'bg-surface-raised'}`}
                  >
                    {i + 1}
                  </button>
                ))}
              </div>
            )}

            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void download()}>{album && !story ? 'تنزيلُ الصور' : 'تنزيلُ الصورة'}</Button>
              <Button onClick={() => { void navigator.clipboard.writeText(text).then(() => setCopied(true)) }}>
                {copied ? 'نُسخ ✓' : 'نسخُ النص'}
              </Button>
            </div>
            <p className="text-xs text-ink-soft">للواتساب وأيّ منصّةٍ غيرِ مربوطة: نزّل الصورةَ وانسخ النصّ.</p>
          </div>
        </div>

        {/* ⚠ الشرائحُ كلُّها مرسومةٌ بمقاسها الحقيقيّ خارج الشاشة — منها تُصنع صورُ الألبوم. */}
        <div aria-hidden="true" style={{ position: 'fixed', left: -100000, top: 0, pointerEvents: 'none' }}>
          {slides.map((s, i) => (
            <div key={s.productId} ref={(el) => { slideRefs.current[i] = el }} style={{ width: w, height: h }}>
              <PostDesign d={designFor(s, i)} />
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="النشر">
        {accounts.length === 0 ? (
          <Banner tone="info">
            لا حسابَ مربوطًا — <Link className="underline" to="/social">اربط فيسبوك وإنستغرام</Link>، أو نزّل الصورةَ وانشرها بيدك.
          </Banner>
        ) : (
          <div className="flex flex-wrap gap-4">
            {accounts.map((a) => (
              <label key={a.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={targets.includes(a.id)}
                  onChange={(e) => setChosen(e.target.checked ? [...targets, a.id] : targets.filter((x) => x !== a.id))}
                />
                {platformName[a.platform]} · {a.username ? `@${a.username}` : a.name}
              </label>
            ))}
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-4 text-sm">
          <label className="flex items-center gap-2">
            <input type="radio" checked={!later} onChange={() => setLater(false)} /> الآن
          </label>
          <label className="flex items-center gap-2">
            <input type="radio" checked={later} onChange={() => setLater(true)} /> لاحقًا
          </label>
          {later && (
            <input
              type="datetime-local"
              className="rounded-lg border border-line bg-surface px-3 py-1.5"
              value={scheduleAt}
              onChange={(e) => setScheduleAt(e.target.value)}
            />
          )}
        </div>

        <ErrorBanner error={send.error} />

        <div className="mt-3">
          <Button
            tone="primary"
            disabled={send.isPending || targets.length === 0 || text.trim() === ''}
            onClick={() => send.mutate(later ? 'later' : 'now')}
          >
            {send.isPending ? (later ? 'يُجدول…' : 'يُنشر…') : later ? 'جدوِل' : story ? 'انشر الستوري' : album ? 'انشر الألبوم' : 'انشر الآن'}
          </Button>
        </div>

        {result && <div className="mt-3"><PostResults post={history.data?.find((x) => x.id === result.id) ?? result} compact /></div>}
      </Panel>

      <Panel title="سجلُّ منشورات هذا الصنف">
        {history.data && history.data.length === 0 && <p className="text-sm text-ink-soft">لم يُنشر من قبل.</p>}
        <div className="grid gap-2">
          {history.data?.map((x) => <PostResults key={x.id} post={x} />)}
        </div>
      </Panel>
    </>
  )
}

function Swatches({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return (
    <span className="flex items-center gap-1.5">
      {SWATCHES.map((c) => (
        <button
          key={c}
          type="button"
          aria-label={c}
          onClick={() => onChange(c)}
          className={`h-7 w-7 rounded-full border ${value === c ? 'ring-2 ring-brand ring-offset-2' : 'border-line'}`}
          style={{ background: c }}
        />
      ))}
      <input type="color" value={value} onChange={(e) => onChange(e.target.value)} className="h-7 w-9" />
    </span>
  )
}
